// components/notifications/NotificationSystem.js
import React, { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import notificationService from "../../services/notificationService";
import NotificationItem from "./NotificationItem";
import { useUser } from "../../contexts/UserContext";

/**
 * NotificationSystem component
 * Displays toast notifications and manages their lifecycle
 */
const NotificationSystem = () => {
  const { user } = useUser();
  const [notifications, setNotifications] = useState([]);
  const [mounted, setMounted] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(64); // Default header height

  // Set mounted flag and measure header height
  useEffect(() => {
    setMounted(true);

    // Measure the header height for accurate positioning
    const header =
      document.querySelector("header") || document.querySelector(".header");
    if (header) {
      const headerRect = header.getBoundingClientRect();
      setHeaderHeight(headerRect.height);
    }

    // Set up a resize observer to track header height changes
    if (window.ResizeObserver) {
      const header =
        document.querySelector("header") || document.querySelector(".header");
      if (header) {
        const resizeObserver = new ResizeObserver((entries) => {
          for (let entry of entries) {
            setHeaderHeight(entry.contentRect.height);
          }
        });

        resizeObserver.observe(header);
        return () => resizeObserver.disconnect();
      }
    }
  }, []);

  // Subscribe to notification updates and manage lifecycle
  useEffect(() => {
    if (!user?.uid) return;

    // Start listening for notifications
    notificationService.startListening(user.uid);

    // Subscribe to notification updates
    const unsubscribe = notificationService.subscribe(
      "notification-system",
      (updatedNotifications) => {
        setNotifications(updatedNotifications);
      }
    );

    // Clean up expired notifications
    notificationService.cleanupExpiredNotifications(user.uid);

    // Cleanup old notifications (older than a month or keep only 30)
    notificationService.cleanupOldNotifications(user.uid, 30, 30); // 30 days, max 30 notifications

    return () => {
      unsubscribe();
      notificationService.stopListening();
    };
  }, [user]);

  // Filter active notifications (status = "active")
  const activeNotifications = notifications.filter(
    (n) => n.status === "active"
  );

  // Handle notification close
  const handleClose = (params) => {
    if (params && typeof params === "object" && params.id) {
      const { id, markAsRead } = params;
      notificationService.closeNotification(id, { markAsRead });
    }
  };

  if (!mounted || !user) return null;

  return (
    <div
      className="fixed right-0 z-40 w-96 mr-[23px] flex flex-col items-end pointer-events-none"
      style={{ top: `${headerHeight + 8}px` }} // Position right below header with 8px gap
    >
      <AnimatePresence initial={false}>
        {activeNotifications.map((notification, index) => (
          <motion.div
            key={notification.id}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{
              opacity: 0,
              x: 100,
              transition: {
                type: "spring",
                damping: 20,
                stiffness: 300,
              },
            }}
            transition={{
              type: "spring",
              damping: 25,
              stiffness: 300,
              delay: index * 0.1,
            }}
            className="mb-3 pointer-events-auto w-full"
          >
            <NotificationItem
              notification={notification}
              onClose={handleClose}
              onDismiss={(id) => {
                notificationService.dismissNotification(id);
              }}
              onRead={(id) => notificationService.markAsRead(id)}
            />
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
};

export default NotificationSystem;
